import { Tooltip } from "antd"
import React from "react"
import { useTranslation } from "react-i18next"
import { Cell, Pie, PieChart, ResponsiveContainer } from "recharts"

import type {
  AnalyticsRecordsByTypeRow,
  EntityType
} from "../redux/global-map.types"
import { ANALYTICS_ENTITY_COLOR } from "./analytics-constants"

interface RecordsByTypeDonutProps {
  rows: AnalyticsRecordsByTypeRow[]
  onSelectEntity: (entityType: EntityType) => void
}

/**
 * Spec §4: center shows Total Records (the sum of AVAILABLE entities only —
 * Workforce's `count` is `null`, and an unknown quantity cannot be folded
 * into a total or a percentage-of-total without silently deflating every
 * other slice's real share). Clicking a slice applies that entity's layer
 * filter (spec: "applies the corresponding entity filter") — only
 * lead/project are real `EntityType` layers today, so Subcontractor/
 * Appointment/Workforce rows render but aren't clickable.
 */
export function RecordsByTypeDonut({
  rows,
  onSelectEntity
}: RecordsByTypeDonutProps) {
  const { t } = useTranslation()
  const available = rows.filter((row) => row.available && row.count !== null)
  const total = available.reduce((sum, row) => sum + (row.count ?? 0), 0)

  const chartData = available.map((row) => ({
    key: row.entityType,
    value: row.count ?? 0,
    color: ANALYTICS_ENTITY_COLOR[row.entityType] ?? row.color
  }))

  const clickableEntity = (entityType: string): EntityType | null =>
    entityType === "leads"
      ? "lead"
      : entityType === "projects"
        ? "project"
        : null

  return (
    <div className="flex items-center gap-4">
      <div className="relative size-[140px] shrink-0">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={chartData}
              dataKey="value"
              nameKey="key"
              innerRadius={44}
              outerRadius={68}
              paddingAngle={1}
              isAnimationActive={false}
            >
              {chartData.map((entry) => {
                const entityType = clickableEntity(entry.key)
                return (
                  <Cell
                    key={entry.key}
                    fill={entry.color}
                    cursor={entityType ? "pointer" : "default"}
                    onClick={() => entityType && onSelectEntity(entityType)}
                    role={entityType ? "button" : undefined}
                    tabIndex={entityType ? 0 : undefined}
                  />
                )
              })}
            </Pie>
          </PieChart>
        </ResponsiveContainer>
        <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-xl font-bold text-gray-900">{total}</span>
          <span className="text-[10px] text-slate-500">
            {t("globalMap.analytics.recordsByType.totalRecords")}
          </span>
        </div>
      </div>

      <ul className="m-0 flex min-w-0 flex-1 list-none flex-col gap-1.5 p-0">
        {rows.map((row) => {
          const entityType = clickableEntity(row.entityType)
          const label = t(`globalMap.analytics.recordsByType.${row.entityType}`)
          const content = (
            <div className="flex items-center gap-2 text-xs">
              <span
                aria-hidden
                className="size-2.5 shrink-0 rounded-full"
                style={{
                  backgroundColor: row.available
                    ? (ANALYTICS_ENTITY_COLOR[row.entityType] ?? row.color)
                    : "#cbd5e1"
                }}
              />
              <span className="min-w-0 flex-1 truncate text-slate-700">
                {label}
              </span>
              <span className="font-semibold text-gray-900">
                {row.available
                  ? row.count
                  : t("globalMap.analytics.dataUnavailableShort")}
              </span>
              {row.available && (
                <span className="w-9 text-right text-slate-400">
                  {row.percent}%
                </span>
              )}
            </div>
          )

          return (
            <li key={row.entityType}>
              {entityType ? (
                <button
                  type="button"
                  onClick={() => onSelectEntity(entityType)}
                  className="w-full rounded px-1 py-0.5 text-left hover:bg-gray-50"
                  aria-label={t("globalMap.analytics.recordsByType.filterBy", {
                    label
                  })}
                >
                  {content}
                </button>
              ) : !row.available ? (
                <Tooltip title={t("globalMap.tabs.comingSoon")}>
                  <div className="px-1 py-0.5">{content}</div>
                </Tooltip>
              ) : (
                <div className="px-1 py-0.5">{content}</div>
              )}
            </li>
          )
        })}
      </ul>
    </div>
  )
}
